import { formatDate } from "@clipboard-health/date-time";
import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, Stack } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { Button } from "@src/appV2/redesign/components/Button";
import { formatDurationLabel } from "@src/appV2/redesign/utils/formatDurationLabel";

import { ShiftDetailsInnerCard } from "./InnerCard";

interface CancellationPolicyCardProps {
  workplaceLateCancellationPeriod?: number;
  workplaceLateCancellationFeeHours?: number;
  workplaceSentHomeChargeHours?: number;
  onViewWorkplacePolicies: () => void;
  lastUpdatedAt?: Date;
}

export function CancellationPolicyCard(props: CancellationPolicyCardProps) {
  const {
    workplaceLateCancellationPeriod,
    workplaceLateCancellationFeeHours,
    workplaceSentHomeChargeHours,
    onViewWorkplacePolicies,
    lastUpdatedAt,
  } = props;

  const isUnifyLateCancellationPolicyAndPayoutEnabled = useCbhFlag(
    CbhFeatureFlag.UNIFY_LATE_CANCELLATION_POLICY_AND_PAYOUT,
    {
      defaultValue: false,
    }
  );
  const workerHasLateCancelProtection = isUnifyLateCancellationPolicyAndPayoutEnabled;

  const hasLateCancellationPayout =
    isDefined(workplaceLateCancellationPeriod) &&
    isDefined(workplaceLateCancellationFeeHours) &&
    workplaceLateCancellationPeriod > 0 &&
    workplaceLateCancellationFeeHours > 0;

  const hasSentHomePayout =
    isDefined(workplaceSentHomeChargeHours) && workplaceSentHomeChargeHours > 0;

  const labelFormatOptions = { keepOriginalUnit: true };
  const lateCancellationPeriodText = formatDurationLabel(
    workplaceLateCancellationPeriod ?? 0,
    "hour",
    labelFormatOptions
  );
  const lateCancellationFeeHoursText = formatDurationLabel(
    workplaceLateCancellationFeeHours ?? 0,
    "hour",
    labelFormatOptions
  );
  const sentHomeChargeHoursText = formatDurationLabel(
    workplaceSentHomeChargeHours ?? 0,
    "hour",
    labelFormatOptions
  );

  if (!hasLateCancellationPayout && !hasSentHomePayout) {
    return null;
  }

  return (
    <ShiftDetailsInnerCard
      footer={
        <Stack spacing={4}>
          <Button fullWidth variant="outlined" size="medium" onClick={onViewWorkplacePolicies}>
            View workplace policies
          </Button>
        </Stack>
      }
    >
      <CardContent sx={{ paddingY: 2, paddingX: 2 }}>
        <Stack spacing={4} sx={{ paddingX: 4, paddingY: 4 }}>
          <Text semibold variant="h5">
            Cancellation Payouts
          </Text>

          {workerHasLateCancelProtection && (
            <Text variant="body2" sx={{ color: (theme) => theme.palette.text.primary }}>
              Shifts cancelled by the workplace within 2 hours of the shift are covered by our Late
              Cancellation Protection.
            </Text>
          )}

          {!workerHasLateCancelProtection && hasLateCancellationPayout && (
            <Text variant="body2" sx={{ color: (theme) => theme.palette.text.primary }}>
              Shifts cancelled by this workplace within {lateCancellationPeriodText} before shift
              start will lead to a {lateCancellationFeeHoursText} payout for the scheduled worker.
            </Text>
          )}
          {hasSentHomePayout && (
            <Text variant="body2" sx={{ color: (theme) => theme.palette.text.primary }}>
              Shifts cancelled after the scheduled worker has arrived at the workplace will lead to
              a {sentHomeChargeHoursText} payout.
            </Text>
          )}
          {isDefined(lastUpdatedAt) && (
            <Text variant="caption" sx={{ color: (theme) => theme.palette.text.secondary }}>
              As of {formatDate(lastUpdatedAt)}
            </Text>
          )}
        </Stack>
      </CardContent>
    </ShiftDetailsInnerCard>
  );
}
